import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

interface UseSelectionOptions<T> {
  data: T[];
  getRowId?: (row: T, index: number) => string;
  onSelectionChange?: (selectedRows: T[]) => void;
}

interface UseSelectionReturn<T> {
  selectedIds: Set<string>;
  isSelected: (row: T, index?: number) => boolean;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  toggleRow: (row: T, index?: number) => void;
  toggleAll: () => void;
  clearSelection: () => void;
  selectedRows: T[];
  selectedCount: number;
}

export function useSelection<T extends Record<string, unknown>>({
  data,
  getRowId,
  onSelectionChange
}: UseSelectionOptions<T>): UseSelectionReturn<T> {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Track selected count for O(1) access
  const selectedCountRef = useRef<number>(0);
  const [selectedCount, setSelectedCount] = useState<number>(0);

  // Helper function to get row ID - defined outside to be pure
  function getIdForRow(row: T, index: number, getRowIdFn?: (row: T, index: number) => string): string {
    if (getRowIdFn) {
      return getRowIdFn(row, index);
    }
    // Try common ID fields first (O(1) lookup)
    if ('id' in row && row.id !== null && row.id !== undefined) {
      return String(row.id);
    }
    if ('_id' in row && row._id !== null && row._id !== undefined) {
      return String(row._id);
    }
    if ('key' in row && row.key !== null && row.key !== undefined) {
      return String(row.key);
    }
    // Use index-based ID as fallback (fast, stable for static data)
    // This is much faster than JSON.stringify
    return `__row_${index}`;
  }

  // Memoized getIdForRow callback for use in other callbacks
  const getIdForRowCallback = useCallback((row: T, index?: number): string => {
    const actualIndex = index ?? data.indexOf(row);
    return getIdForRow(row, actualIndex, getRowId);
  }, [data, getRowId]);

  const isSelected = useCallback((row: T, index?: number): boolean => {
    const id = getIdForRowCallback(row, index);
    return selectedIds.has(id);
  }, [selectedIds, getIdForRowCallback]);

  // O(1) check instead of O(n)
  const isAllSelected = useMemo(() => {
    return data.length > 0 && selectedCount === data.length;
  }, [data.length, selectedCount]);

  // O(1) check instead of O(n)
  const isPartiallySelected = useMemo(() => {
    return selectedCount > 0 && selectedCount < data.length;
  }, [selectedCount, data.length]);

  // Compute selected rows only when needed (lazy evaluation)
  const selectedRows = useMemo(() => {
    if (selectedIds.size === 0) return [];
    return data.filter((row, index) => selectedIds.has(getIdForRowCallback(row, index)));
  }, [data, selectedIds, getIdForRowCallback]);

  // Sync selected count when data changes
  useEffect(() => {
    if (selectedIds.size === 0) {
      selectedCountRef.current = 0;
      setSelectedCount(0);
      return;
    }

    // Recount valid selections when data changes
    let validCount = 0;
    const validIds = new Set<string>();

    data.forEach((row, index) => {
      const id = getIdForRow(row, index, getRowId);
      if (selectedIds.has(id)) {
        validCount++;
        validIds.add(id);
      }
    });

    if (validCount !== selectedIds.size) {
      // Some selected rows are no longer in data, clean up
      setSelectedIds(validIds);
    }

    selectedCountRef.current = validCount;
    setSelectedCount(validCount);
  }, [data, getRowId, selectedIds]);

  const toggleRow = useCallback((row: T, index?: number) => {
    const id = getIdForRowCallback(row, index);
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        selectedCountRef.current = Math.max(0, selectedCountRef.current - 1);
      } else {
        newSet.add(id);
        selectedCountRef.current = selectedCountRef.current + 1;
      }
      setSelectedCount(selectedCountRef.current);

      // Calculate new selected rows and notify
      if (onSelectionChange) {
        const newSelectedRows = data.filter((r, i) => newSet.has(getIdForRowCallback(r, i)));
        onSelectionChange(newSelectedRows);
      }

      return newSet;
    });
  }, [data, getIdForRowCallback, onSelectionChange]);

  const toggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedIds(new Set());
      selectedCountRef.current = 0;
      setSelectedCount(0);
      onSelectionChange?.([]);
    } else {
      const allIds = new Set(data.map((row, index) => getIdForRowCallback(row, index)));
      setSelectedIds(allIds);
      selectedCountRef.current = data.length;
      setSelectedCount(data.length);
      onSelectionChange?.(data);
    }
  }, [data, isAllSelected, getIdForRowCallback, onSelectionChange]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    selectedCountRef.current = 0;
    setSelectedCount(0);
    onSelectionChange?.([]);
  }, [onSelectionChange]);

  return {
    selectedIds,
    isSelected,
    isAllSelected,
    isPartiallySelected,
    toggleRow,
    toggleAll,
    clearSelection,
    selectedRows,
    selectedCount
  };
}
